import { httpClient } from '@/shared/api/httpClient';

export type StatusLicenca =
  | 'trial'
  | 'ativa'
  | 'vencida'
  | 'bloqueada'
  | 'desconhecida'
  | 'sem-acesso';

export type EstadoLicenca = {
  status: StatusLicenca;
  plano: string | null;
  validaAte: string | null;
  emitidoEm: string | null;
  observacao: string | null;
  email: string | null;
  renovacaoAutomatica: boolean;
  ultimoCheckEm: string | null;
  origem: 'servidor' | 'cache' | 'sem-cache' | 'sem-token' | 'trial-auth';
  motivoBloqueio: string | null;
  urlConsultada: string | null;
  ultimoErro: string | null;
};

export type ResultadoReconciliacao =
  | { tipo: 'ok'; aplicados: Array<{ id: string; dias: number }> }
  | { tipo: 'sem-sessao' }
  | { tipo: 'sem-pagamentos'; aplicados: [] }
  | { tipo: 'conflito-email' }
  | { tipo: 'falha'; motivo: string };

export type ResultadoPausar =
  | { ok: true; cancelouNoMP: boolean }
  | { erro: string; codigo?: string };

export type ResultadoVerificarMP =
  | {
      // Status cru do preapproval no MP. null = não há assinatura
      // recorrente vinculada a este e-mail.
      statusMP: 'authorized' | 'paused' | 'cancelled' | 'pending' | null;
      ativaNoMP: boolean;
      estado: EstadoLicenca;
    }
  | { erro: string };

export const licencaApi = {
  status: () => httpClient.get<EstadoLicenca>('/api/licenca'),
  // Recheck "agressivo" — limpa cache, refaz fetch, e tenta auto-registro se
  // estiver em erro de auth. O servidor decide tudo; o cliente só dispara.
  recheck: () => httpClient.post<EstadoLicenca>('/api/licenca/recheck', {}),
  // Chamado uma vez logo após o login bem-sucedido — aplica pagamentos
  // pendentes do email do usuário na licença desta máquina.
  reconciliar: () =>
    httpClient.post<ResultadoReconciliacao>('/api/licenca/reconciliar', {}),
  // Cancela o preapproval no Mercado Pago. O ciclo já pago segue válido
  // até vencer — depois disso o app exibe novamente os banners de
  // vencimento e o usuário precisa assinar de novo para continuar.
  pausarRenovacao: () =>
    httpClient.post<ResultadoPausar>('/api/licenca/pausar', {}),
  // Consulta o status real da assinatura no Mercado Pago (fonte de
  // verdade) e sincroniza a flag local. Útil quando a renovação não
  // aparece como esperado por causa de um webhook perdido.
  verificarMP: () =>
    httpClient.post<ResultadoVerificarMP>('/api/licenca/verificar-mp', {}),
};

export function licencaPermiteUso(estado: EstadoLicenca | null): boolean {
  if (!estado) return false;
  return estado.status === 'ativa' || estado.status === 'trial' || estado.status === 'vencida';
}

export function licencaEhAviso(estado: EstadoLicenca | null): boolean {
  return Boolean(estado && (estado.status === 'vencida' || estado.status === 'trial'));
}

export function diasAteVencimento(validaAte: string | null): number | null {
  if (!validaAte) return null;
  const ms = new Date(validaAte).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  return Math.ceil(ms / 86400_000);
}
